import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

// ── KNKT geometric mark ───────────────────────────────────────────────
function KnktMark({ width = 90 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80.77 16.66" width={width}>
      <polygon fill="#ffffff" points="10.58 6.8 18.32 0.02 13.38 0.02 8.54 4.27 5.95 6.54 3.86 8.37 3.86 3.96 3.86 0.02 0.69 0.02 0 0.02 0 11.76 0 16.09 0 16.66 3.86 16.66 3.86 12.7 7.99 9.07 14.12 16.66 18.54 16.66 10.58 6.8" />
      <polygon fill="#ffffff" points="34.75 0.02 34.75 11.29 23.98 0.02 23.95 0 23.95 0.02 20.33 0.02 20.33 1.57 20.33 16.66 23.95 16.66 23.95 5.36 34.75 16.65 34.75 16.66 34.76 16.66 38.46 16.66 38.46 15.17 38.46 0.02 34.75 0.02" />
      <polygon fill="#ffffff" points="53.74 6.8 61.47 0.02 56.53 0.02 51.69 4.27 49.1 6.54 47.02 8.37 47.02 3.96 47.02 0.02 43.84 0.02 43.16 0.02 43.16 11.76 43.16 16.09 43.16 16.66 47.02 16.66 47.02 12.7 51.15 9.07 57.27 16.66 61.69 16.66 53.74 6.8" />
      <polygon fill="#ffffff" points="73.88 0.02 70.02 0.02 63.14 0.02 63.14 3.49 70.02 3.49 70.02 7.82 70.02 9.04 70.02 16.66 73.88 16.66 73.88 9.04 73.88 7.82 73.88 3.49 80.77 3.49 80.77 0.02 73.88 0.02" />
    </svg>
  );
}

// ── Canvas dot-wave background ────────────────────────────────────────
function CanvasBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const ctx = canvas.getContext('2d');
    const dotSize = 1.5;
    const dotSpacing = 24;
    let progress = 0;
    let animId;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cols = Math.ceil(canvas.width / dotSpacing) + 1;
      const rows = Math.ceil(canvas.height / dotSpacing) + 1;
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      const maxDist = Math.sqrt(cx * cx + cy * cy);

      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = i * dotSpacing;
          const y = j * dotSpacing;
          const dist = Math.sqrt((x - cx) * (x - cx) + (y - cy) * (y - cy));
          const wave = Math.sin((dist / maxDist) * Math.PI * 4 - progress) * 0.5 + 0.5;
          const opacity = 0.07 + wave * 0.22;
          const usePurple = (i + j) % 7 === 0;
          ctx.fillStyle = usePurple
            ? `rgba(168,85,247,${(opacity * 0.7).toFixed(3)})`
            : `rgba(0,217,255,${opacity.toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(x, y, dotSize, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      progress += 0.03;
      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 0, pointerEvents: 'none', display: 'block' }}
    />
  );
}

// ── Cycling tagline ───────────────────────────────────────────────────
const TAGLINES = ['Every Fan.', 'Every Performance.', 'Every Data Point.', 'Every Connection.', 'Every Show.', 'Every Release.', 'Everything Music.'];
const SHAKE_X = [0, -7, 7, -7, 7, -4, 4, -2, 2, 0];
const SHAKE_SCALE = [1, 1.12, 1.12, 1.1, 1.1, 1.06, 1.06, 1.03, 1.03, 1];
const SHAKE_TIMES = [0, 0.1, 0.2, 0.3, 0.4, 0.55, 0.65, 0.75, 0.85, 1];

function AnimatedTagline() {
  const [active, setActive] = useState(0);
  const isEverything = TAGLINES[active] === 'Everything Music.';

  useEffect(() => {
    const delay = isEverything ? 4000 : 2200;
    const t = setTimeout(() => setActive(i => (i + 1) % TAGLINES.length), delay);
    return () => clearTimeout(t);
  }, [active, isEverything]);

  return (
    <div style={{ height: 36, overflow: 'hidden', position: 'relative' }}>
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ y: 28, opacity: 0, x: 0 }}
          animate={{ y: 0, opacity: 1, x: isEverything ? SHAKE_X : 0, scale: isEverything ? SHAKE_SCALE : 1 }}
          exit={{ y: -28, opacity: 0 }}
          transition={{
            y: { duration: 0.4, ease: 'easeOut' },
            opacity: { duration: 0.4, ease: 'easeOut' },
            x: isEverything ? { delay: 0.55, duration: 0.55, times: SHAKE_TIMES, ease: 'easeInOut' } : { duration: 0 },
            scale: isEverything ? { delay: 0.55, duration: 0.55, times: SHAKE_TIMES, ease: 'easeInOut' } : { duration: 0 },
          }}
          style={{
            position: 'absolute',
            width: '100%',
            textAlign: 'center',
            fontSize: 20,
            fontWeight: 600,
            color: isEverything ? '#00D9FF' : 'rgba(255,255,255,0.75)',
            letterSpacing: '0.02em',
          }}
        >
          {TAGLINES[active]}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ── Login Page ────────────────────────────────────────────────────────
function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = loading || email.length === 0 || password.length === 0;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#06060a', position: 'relative', overflow: 'hidden', padding: '40px 20px', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <CanvasBackground />

      {/* Logo + tagline */}
      <motion.div
        style={{ position: 'relative', zIndex: 1, textAlign: 'center', marginBottom: 36 }}
        initial={{ opacity: 0, scale: 0.88 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32, filter: 'drop-shadow(0 0 16px rgba(255,255,255,0.12))' }}>
          <KnktMark width={Math.min(window.innerWidth * 0.55, 400)} />
        </div>
        <AnimatedTagline />
      </motion.div>

      {/* Login card */}
      <motion.div
        style={{ width: '100%', maxWidth: 420, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '36px 40px', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', position: 'relative', zIndex: 1, boxSizing: 'border-box' }}
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 4px', background: 'linear-gradient(135deg, #00D9FF 0%, #A855F7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', textAlign: 'center' }}>
          Welcome back
        </h2>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', textAlign: 'center', margin: '0 0 28px' }}>
          Sign in to Email Manager
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label htmlFor="email" style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your KNKT email"
              required
              autoComplete="email"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10, color: '#fff', fontSize: 14, padding: '13px 16px', width: '100%', outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s', boxSizing: 'border-box' }}
              onFocus={e => { e.target.style.borderColor = 'rgba(0,217,255,0.55)'; e.target.style.boxShadow = '0 0 0 3px rgba(0,217,255,0.1)'; }}
              onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.14)'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="password" style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10, color: '#fff', fontSize: 14, padding: '13px 16px', width: '100%', outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s', boxSizing: 'border-box' }}
              onFocus={e => { e.target.style.borderColor = 'rgba(0,217,255,0.55)'; e.target.style.boxShadow = '0 0 0 3px rgba(0,217,255,0.1)'; }}
              onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.14)'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          {error && (
            <div style={{ fontSize: 13, color: '#EC4899', marginBottom: 12, textAlign: 'center' }}>{error}</div>
          )}

          <motion.button
            type="submit"
            disabled={isDisabled}
            style={{ width: '100%', background: 'linear-gradient(135deg, #00D9FF 0%, #A855F7 100%)', border: 'none', borderRadius: 10, color: '#000', fontSize: 15, fontWeight: 700, padding: '14px', cursor: isDisabled ? 'not-allowed' : 'pointer', letterSpacing: '0.02em', boxShadow: isDisabled ? 'none' : '0 0 32px rgba(0,217,255,0.35)', opacity: isDisabled ? 0.35 : 1, marginTop: 8, transition: 'opacity 0.2s' }}
            whileHover={!isDisabled ? { boxShadow: '0 0 48px rgba(0,217,255,0.55)' } : {}}
            whileTap={!isDisabled ? { scale: 0.98 } : {}}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
}

export default LoginPage;
